import React, { useMemo } from "react";
import ProgressGauge from "./ProgressGauge";

const d = (n) => `${Number(n || 0)} days`;

export default function CurrentProjectCard({ project, overall }) {
  const remainingDays = Number(project?.progressData?.remainingDuration ?? 0);
  const durationDays = Number(project?.scheduleData?.projectDuration ?? 0);
  const elapsed = Math.max(0, durationDays - remainingDays);
  const elapsedPct = durationDays ? Math.min(100, Math.round((elapsed / durationDays) * 100)) : 0;

  const meta = useMemo(
    () =>
      [
        { label: "Location", value: project?.location },
        { label: "Service Provider", value: project?.serviceProvider },
        { label: "Service Type", value: project?.serviceType },
      ].filter((m) => m.value),
    [project]
  );

  return (
    <section
      className="bg-white rounded-2xl border border-gray-100 p-4 sm:p-6 shadow-sm hover:shadow-md transition-shadow"
      aria-label="Current Project"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Current Project</h2>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span
            className="inline-flex items-center gap-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100 px-3 py-1 text-xs font-semibold"
            title="Remaining duration"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-teal-500" />
            {d(remainingDays)} left
          </span>
          <span className="text-sm font-semibold text-teal-600">
            {project?.propertyType} • {project?.name}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="flex items-center justify-center">
          <ProgressGauge percent={overall} />
        </div>

        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Schedule Data</h3>
          <dl className="text-sm text-gray-700 space-y-1">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Start Date:</dt>
              <dd className="font-medium">{project?.scheduleData?.startDate || "-"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Finishing Date:</dt>
              <dd className="font-medium">{project?.scheduleData?.finishDate || "-"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Project Duration:</dt>
              <dd className="font-medium">{d(durationDays)}</dd>
            </div>
          </dl>

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Elapsed</span>
              <span>Remaining</span>
            </div>
            <div className="h-2.5 w-full bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-500"
                style={{ width: `${elapsedPct}%` }}
                aria-label={`Elapsed ${elapsedPct}%`}
              />
            </div>
            <div className="mt-1 text-xs text-gray-500 flex justify-between">
              <span>{d(elapsed)}</span>
              <span>{d(remainingDays)}</span>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-gray-800 mb-3">Progress Data</h3>
          <dl className="text-sm text-gray-700 space-y-1">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Actual Start:</dt>
              <dd className="font-medium">{project?.progressData?.actualStartDate || "-"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Expected Finish:</dt>
              <dd className="font-medium">{project?.progressData?.expectedFinishDate || "-"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Expected Duration:</dt>
              <dd className="font-medium">{d(project?.progressData?.expectedDuration ?? 0)}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">Remaining Duration:</dt>
              <dd className="font-medium">{d(remainingDays)}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="my-6 h-px bg-gray-100" />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 text-sm">
        {meta.map((m) => (
          <div key={m.label} className="text-gray-700">
            <span className="text-gray-500">{m.label}:</span>{" "}
            <span className="font-medium">{m.value}</span>
          </div>
        ))}

        <div className="flex items-start lg:justify-end">
          <div className="min-w-0">
            <div className="text-gray-500 mb-2">Areas:</div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar py-0.5 pr-1 -mr-1">
              {(project?.areaDefinitions || []).map((a) => (
                <span
                  key={a}
                  className="shrink-0 px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium border border-gray-200"
                  title={a}
                >
                  {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
